/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useChatMessages } from '@ai-sdk-tools/store';
import { NetworkStatus, useQuery } from '@apollo/client';
import { useIsMobile } from '@newfront-insurance/admin-ui';
import { isEmpty, isNotEmpty } from '@newfront-insurance/core';
import { CircleSpinnerInline, colors, IconAdd2, SegmentedControlV2, useFuzzy } from '@newfront-insurance/core-ui';
import {
  cn,
  IconButtonAsButton,
  IconSearch,
  NewfrontLogo,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@newfront-insurance/core-ui/v2';
import { Settings, X } from 'lucide-react';
import { createContext, Fragment, type ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { IconOutlook, IconSidebar } from './icons';
import { OrphanedStreamsContainer } from './orphaned-streams-container';
import type { ChatAppType, GetChatsByFilters } from '../../../queries/chats';
import { GET_CHATS_BY_FILTERS } from '../../../queries/chats';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { groupChatsByDate } from '../lib/chat-utils';
import { normalizeChatUuid } from '../lib/notification-helpers';

const SIDEBAR_TRANSITION_DURATION = 200;

interface ChatSidebarContextValue {
  isSidebarExpanded: boolean;
  /** True only after the width transition completes when expanding. Use for text/content rendering to prevent reflow. */
  isContentExpanded: boolean;
  chatHistoryFilter: 'current-account' | 'all-accounts';
  setChatHistoryFilter: (value: 'current-account' | 'all-accounts') => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  searchResults: GetChatsByFilters;
  isLoadingChats: boolean;
  isSearchActive: boolean;
  setIsSearchActive: (active: boolean) => void;
}

const ChatSidebarContext = createContext<ChatSidebarContextValue | null>(null);

function useChatSidebarContext() {
  const context = useContext(ChatSidebarContext);
  if (!context) {
    throw new Error('ChatSidebar components must be used within ChatSidebar');
  }
  return context;
}

interface ChatSidebarProps {
  className?: string;
  children?: ReactNode;
}

/**
 * Chat sidebar component with history, search, and filtering.
 *
 * On desktop, the sidebar is always visible in one of two states:
 * - **Collapsed** (63px): Shows only icon buttons (New Chat, Settings, Search)
 * - **Expanded** (253px): Shows full sidebar with labels, chat history, and filters
 *
 * On mobile, the sidebar preserves the previous overlay behavior:
 * hidden by default, shown as an overlay when toggled.
 *
 * The `isSidebarOpen` context value controls:
 * - Desktop: collapsed (false) vs expanded (true)
 * - Mobile: hidden (false) vs overlay (true)
 */
export function ChatSidebar({ className, children }: ChatSidebarProps) {
  const { isMobile: isMobileViewport } = useIsMobile();
  const {
    userUuid,
    accountUuid,
    setIsSidebarOpen,
    isSidebarOpen,
    isFullscreen,
    isStandaloneMode,
    effectiveAppType,
    appType,
    hasNotifications,
  } = useChatOverlayContext();

  // In add-in mode (e.g. Outlook), the panel is narrow but should not
  // use mobile behavior — the sidebar should collapse with animation, not unmount.
  const isAddin = appType === 'OUTLOOK';
  const isMobile = isAddin ? false : isMobileViewport;

  const isSidebarExpanded = isSidebarOpen;

  // Delay expanded content rendering to avoid text reflow during the width transition.
  // When expanding: wait for the width transition to finish before showing text content.
  // When collapsing: immediately hide text content so it doesn't reflow as width shrinks.
  const [isContentExpanded, setIsContentExpanded] = useState(isSidebarExpanded);

  useEffect(() => {
    if (isSidebarExpanded) {
      const timer = setTimeout(() => setIsContentExpanded(true), SIDEBAR_TRANSITION_DURATION);
      return () => clearTimeout(timer);
    }
    setIsContentExpanded(false);
  }, [isSidebarExpanded]);

  const [chatHistoryFilter, setChatHistoryFilter] = useState<'current-account' | 'all-accounts'>(
    isEmpty(accountUuid) ? 'all-accounts' : 'current-account',
  );

  const [isSearchActive, setIsSearchActive] = useState(false);

  // Fetch chats for filtering and search
  const { data, networkStatus } = useQuery(GET_CHATS_BY_FILTERS, {
    variables: {
      userUuid,
      appType: effectiveAppType,
    },
    skip: isEmpty(userUuid),
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const { chatsList: chats = [] } = data ?? {};
  const isLoadingChats = networkStatus === NetworkStatus.loading && data === undefined;

  // Filter chats by account
  const chatsFilteredByAccount = useMemo(() => {
    return chatHistoryFilter === 'current-account' && isNotEmpty(accountUuid)
      ? [...chats].filter((chat) => chat.accountUuid === accountUuid)
      : [...chats];
  }, [chats, accountUuid, chatHistoryFilter]);

  // Apply fuzzy search - useFuzzy manages its own query state
  const fuzzySearch = useFuzzy(chatsFilteredByAccount, {
    keys: ['title', 'summary'],
  });

  const contextValue: ChatSidebarContextValue = useMemo(
    () => ({
      isSidebarExpanded,
      isContentExpanded,
      chatHistoryFilter,
      setChatHistoryFilter,
      searchQuery: fuzzySearch.query,
      setSearchQuery: fuzzySearch.search,
      searchResults: fuzzySearch.results,
      isLoadingChats,
      isSearchActive,
      setIsSearchActive,
    }),
    [
      isSidebarExpanded,
      isContentExpanded,
      chatHistoryFilter,
      fuzzySearch.query,
      fuzzySearch.results,
      fuzzySearch.search,
      isLoadingChats,
      isSearchActive,
    ],
  );

  // Mobile: preserve current behavior (hidden when sidebar is closed)
  if (isMobile && !isSidebarOpen) {
    return null;
  }

  // Default composition for backward compatibility
  const defaultChildren = (
    <>
      <ChatSidebarNewChat />
      <ChatSidebarSearch />
      <ChatSidebarFilterTabs />
      <ChatSidebarHistory />
    </>
  );

  return (
    <ChatSidebarContext.Provider value={contextValue}>
      {isSidebarOpen && (isMobile || isAddin) && (
        <div
          className="fixed inset-0 z-[1299] bg-[#000]/70 opacity-50 backdrop-blur-xs"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      <div
        className={cn('absolute -top-px h-[calc(100%+2px)]', {
          'right-full z-[-1]': !isFullscreen,
          'z-overlay left-0': isFullscreen && !isMobile,
          'z-overlay right-0 left-0': isFullscreen && isMobile,
        })}
      >
        <div
          className={cn(
            'border-border/50 relative flex h-full shrink-0 flex-col rounded-xs rounded-r-none border-y border-l',
            'bg-steel-50 dark:bg-steel-500',
            'overflow-hidden transition-[width] duration-200',
            isSidebarExpanded ? 'w-[253px]' : isAddin ? 'w-0 border-none' : 'w-[63px]',
            !isFullscreen && !isStandaloneMode && 'shadow-floating-bar [clip-path:inset(-20px_0px_-20px_-20px)]',
            className,
          )}
        >
          {/* Sidebar header with toggle */}
          <header
            className={cn(
              'border-border/50 flex h-14 shrink-0 items-center border-b px-[18px]',
              'text-muted-foreground',
              isContentExpanded ? 'justify-between' : 'justify-start',
            )}
          >
            {isContentExpanded && (
              <h1 className="text-muted-foreground animate-in fade-in text-sm font-semibold whitespace-nowrap duration-200">
                Nova Assist
              </h1>
            )}
            <div className="relative">
              <IconButtonAsButton
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="hover:bg-steel-200/20 shrink-0"
              >
                <IconSidebar />
              </IconButtonAsButton>
              {!isContentExpanded && hasNotifications && (
                <span className="bg-rebrand-400 absolute top-0 right-0 size-2 rounded-full" />
              )}
            </div>
          </header>

          {/* Sidebar body */}
          <div className="flex min-h-0 flex-1 flex-col gap-2 px-2 pt-5">{children ?? defaultChildren}</div>

          {/* Standalone mode logo (only when expanded) */}
          {isStandaloneMode && isContentExpanded && (
            <div
              className={cn(
                'pointer-events-none absolute inset-x-0 bottom-0 h-20 rounded-b-xs shadow-lg',
                'from-steel-50 via-steel-50/95 bg-gradient-to-t to-transparent',
                'dark:from-steel-500 dark:via-steel-500/95 dark:to-transparent',
              )}
            >
              <div className="flex h-full items-end justify-center pb-3">
                <NewfrontLogo />
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Renderless container that recovers orphaned streams */}
      {chats.length > 0 && <OrphanedStreamsContainer chats={chats} />}
    </ChatSidebarContext.Provider>
  );
}

/**
 * New Chat button component for the sidebar.
 * Shows icon-only when collapsed, icon + text when expanded.
 */
export function ChatSidebarNewChat() {
  const { startNewChat, setIsSidebarOpen, setIsSettingsOpen, isSettingsOpen } = useChatOverlayContext();
  const { isMobile } = useIsMobile();
  const { isContentExpanded } = useChatSidebarContext();
  const messages = useChatMessages();

  const hasAnyMessages = messages.length > 0;
  const isEnabled = hasAnyMessages || isSettingsOpen;

  return (
    <MenuItem
      icon={<IconAdd2 color={colors.brand[500]} size={16} />}
      className="font-semibold"
      iconClassName="rounded-full"
      iconStyle={{ background: 'linear-gradient(97deg, #A4F5FF 0%, #E7FC53 122.24%)' }}
      onClick={() => {
        startNewChat();
        setIsSettingsOpen(false);
        if (isMobile) {
          setIsSidebarOpen(false);
        }
      }}
      disabled={!isEnabled}
    >
      {isContentExpanded && (
        <span className="text-brand-500 animate-in fade-in pl-1 whitespace-nowrap duration-200">New chat</span>
      )}
    </MenuItem>
  );
}

/**
 * Built-in settings button for the sidebar.
 * Shows icon-only when collapsed, icon + text when expanded.
 * Toggles the settings panel via context.
 */
export function ChatSidebarSettingsButton() {
  const { isSettingsOpen, setIsSettingsOpen, setIsSidebarOpen } = useChatOverlayContext();
  const { isMobile } = useIsMobile();
  const { isContentExpanded } = useChatSidebarContext();

  return (
    <MenuItem
      icon={<Settings className="size-5" />}
      isActive={isSettingsOpen}
      onClick={() => {
        setIsSettingsOpen(!isSettingsOpen);
        if (isMobile) {
          setIsSidebarOpen(false);
        }
      }}
    >
      {isContentExpanded && <span className="animate-in fade-in whitespace-nowrap duration-200">Settings</span>}
    </MenuItem>
  );
}

/**
 * Search component for the sidebar.
 * In collapsed mode: shows search icon that expands the sidebar when clicked.
 * In expanded mode: search is integrated into the history section titles (see ChatSidebarHistory).
 */
export function ChatSidebarSearch() {
  const { setIsSidebarOpen } = useChatOverlayContext();
  const { isContentExpanded, setIsSearchActive } = useChatSidebarContext();

  if (isContentExpanded) {
    return null;
  }

  return (
    <MenuItem
      icon={<IconSearch className="text-steel-200 size-5" />}
      onClick={() => {
        setIsSidebarOpen(true);
        setIsSearchActive(true);
      }}
    />
  );
}

/**
 * Filter tabs component for the sidebar.
 * Hidden when sidebar is collapsed.
 */
export function ChatSidebarFilterTabs() {
  const { accountUuid } = useChatOverlayContext();
  const { isContentExpanded, chatHistoryFilter, setChatHistoryFilter } = useChatSidebarContext();

  if (!isContentExpanded) {
    return null;
  }

  return (
    <div className="mt-2 flex flex-col gap-2">
      <SectionTitle className="px-3">Chat History</SectionTitle>
      <SegmentedControlV2
        options={[
          { label: 'Current account', value: 'current-account', disabled: isEmpty(accountUuid) },
          { label: 'All accounts', value: 'all-accounts', disabled: isEmpty(accountUuid) },
        ]}
        onChange={(value) => {
          setChatHistoryFilter(value as 'current-account' | 'all-accounts');
        }}
        value={isEmpty(accountUuid) ? 'all-accounts' : chatHistoryFilter}
        activeColor="default"
        containerClassname={cn('!border-border/50 !bg-background mx-2 !h-8 !rounded-full', {
          'opacity-50': isEmpty(accountUuid),
        })}
        optionClassname="!rounded-full !px-1.5 !border-transparent [&.isActive]:!bg-steel-100 !text-sm"
      />
    </div>
  );
}

/**
 * Chat history list component for the sidebar.
 * Hidden when sidebar is collapsed.
 * Includes inline search: a magnifying glass on the first date group header
 * that expands into a search input overlay when clicked.
 */
export function ChatSidebarHistory() {
  const { isMobile } = useIsMobile();
  const { chatUuid, setIsSidebarOpen, setActiveChat, setIsSettingsOpen, isStandaloneMode, effectiveAppType } =
    useChatOverlayContext();
  const showAllNovaChats = effectiveAppType === null;
  const {
    isContentExpanded,
    searchQuery,
    setSearchQuery,
    searchResults,
    isLoadingChats,
    isSearchActive,
    setIsSearchActive,
  } = useChatSidebarContext();

  const searchInputRef = useRef<HTMLInputElement>(null);

  const groupedChats = useMemo(() => groupChatsByDate(searchResults), [searchResults]);

  // Auto-focus the search input when opened (also re-trigger when content expands after sidebar transition)
  useEffect(() => {
    if (isSearchActive && isContentExpanded) {
      searchInputRef.current?.focus();
    }
  }, [isSearchActive, isContentExpanded]);

  if (!isContentExpanded) {
    return null;
  }

  const handleCloseSearch = () => {
    setSearchQuery('');
    setIsSearchActive(false);
  };

  return (
    <ScrollArea className={cn('-mx-2 mt-4 min-h-0 flex-1', isStandaloneMode && 'mb-20')}>
      <div className="flex flex-col gap-2 px-2 pb-5">
        {isLoadingChats && (
          <MenuItem icon={<CircleSpinnerInline />} className="text-foreground">
            Loading chats...
          </MenuItem>
        )}
        {/* First section title — always rendered so search has a stable anchor */}
        <SectionTitle>
          {isSearchActive ? (
            <div className="flex flex-1 items-center gap-1.5">
              <IconSearch className="text-steel-300 size-4 shrink-0" />
              <input
                ref={searchInputRef}
                className="text-foreground placeholder:text-steel-300 min-w-0 flex-1 bg-transparent text-sm font-normal tracking-normal outline-none"
                placeholder="Search chats"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button
                type="button"
                onClick={handleCloseSearch}
                className="text-steel-300 hover:text-steel-400 shrink-0"
              >
                <X className="size-3.5" />
              </button>
            </div>
          ) : (
            <>
              <span className="flex-1">{groupedChats[0]?.title ?? 'Today'}</span>
              <button
                type="button"
                onClick={() => setIsSearchActive(true)}
                className="text-steel-300 hover:text-steel-400 shrink-0"
              >
                <IconSearch className="size-4" />
              </button>
            </>
          )}
        </SectionTitle>
        {/* Chat groups — first group title already rendered above */}
        {groupedChats.map((group, groupIndex) => (
          <Fragment key={group.title}>
            {groupIndex > 0 && <SectionTitle>{group.title}</SectionTitle>}
            <div className="flex flex-col gap-2 px-3">
              {group.chats.map((chat) => {
                const isActive = normalizeChatUuid(chat.uuid ?? '') === normalizeChatUuid(chatUuid);
                return (
                  <ChatMenuItem
                    key={chat.uuid}
                    onClick={() => {
                      setActiveChat(chat.uuid!);
                      setIsSettingsOpen(false);
                      if (isMobile) {
                        setIsSidebarOpen(false);
                      }
                    }}
                    isActive={isActive}
                    className="text-foreground"
                    hasNotifications={chat.hasUnreadMessages as boolean}
                    isInProgress={chat.activeStreamId !== null}
                    originAppType={showAllNovaChats && chat.appType === 'OUTLOOK' ? chat.appType : undefined}
                  >
                    {chat.title}
                  </ChatMenuItem>
                );
              })}
            </div>
          </Fragment>
        ))}
        {isSearchActive && groupedChats.length === 0 && !isLoadingChats && (
          <p className="text-steel-300 px-6 text-sm">No results found</p>
        )}
      </div>
    </ScrollArea>
  );
}

/**
 * Settings wrapper component for the sidebar.
 * Provides a slot for apps to add their own settings components.
 * @deprecated Use ChatSidebarSettingsButton for the built-in settings toggle.
 */
export function ChatSidebarSettings({ children }: { children: ReactNode }) {
  const { isContentExpanded } = useChatSidebarContext();

  if (!isContentExpanded) {
    return null;
  }

  return <div className="flex shrink-0 flex-col gap-3">{children}</div>;
}

function AppOriginIcon({ appType }: { appType: ChatAppType }) {
  if (appType === 'OUTLOOK') {
    return <IconOutlook className="text-muted-foreground h-4 w-4" />;
  }

  return null;
}

function SectionTitle({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'sticky top-0 z-10 flex items-center gap-2 px-3 text-sm font-semibold tracking-widest',
        'bg-steel-50 text-steel-300 dark:bg-steel-500 dark:text-steel-400 uppercase',
        className,
      )}
    >
      {children}
    </div>
  );
}

function MenuItem({
  children,
  icon,
  className,
  iconClassName,
  iconStyle,
  onClick,
  disabled,
  isActive,
}: {
  children?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  iconClassName?: string;
  iconStyle?: React.CSSProperties;
  onClick?: () => void;
  disabled?: boolean;
  isActive?: boolean;
}) {
  return (
    <button
      type="button"
      className={cn(
        'text-muted-foreground relative flex items-center gap-2 px-2 py-1.5 text-start',
        'hover:bg-steel-100 dark:hover:bg-steel-400 rounded-xs',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
        {
          'bg-steel-400/10 hover:bg-steel-400/10 cursor-default font-medium': isActive,
          'cursor-default hover:bg-transparent': !onClick,
        },
        className,
      )}
      onClick={isActive ? undefined : onClick}
      disabled={disabled}
    >
      {icon && (
        <div className={cn('flex size-6 shrink-0 items-center justify-center', iconClassName)} style={iconStyle}>
          {icon}
        </div>
      )}
      {children}
    </button>
  );
}

function ChatMenuItem({
  children,
  icon,
  className,
  iconClassName,
  iconStyle,
  onClick,
  disabled,
  isActive,
  hasNotifications,
  isInProgress,
  originAppType,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  iconClassName?: string;
  iconStyle?: React.CSSProperties;
  onClick?: () => void;
  disabled?: boolean;
  isActive?: boolean;
  hasNotifications?: boolean;
  isInProgress?: boolean;
  originAppType?: ChatAppType;
}) {
  return (
    <button
      type="button"
      className={cn(
        'group text-muted-foreground relative grid w-full grid-cols-[3fr_1fr] items-center px-2 py-1.5 text-start',
        'hover:bg-steel-100 dark:hover:bg-steel-400 rounded-xs',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
        {
          'bg-steel-400/10 hover:bg-steel-400/10 cursor-default font-medium': isActive,
          'cursor-default hover:bg-transparent': !onClick,
        },
        className,
      )}
      onClick={isActive ? undefined : onClick}
      disabled={disabled}
    >
      {icon && (
        <div className={cn('flex size-6 shrink-0 items-center justify-center', iconClassName)} style={iconStyle}>
          {icon}
        </div>
      )}
      <div className="min-w-0 break-words">{children}</div>
      <div className="flex items-center justify-end gap-1">
        {hasNotifications && <span className="bg-rebrand-400 h-2 max-h-2 min-h-2 w-2 max-w-2 min-w-2 rounded-full" />}
        {isInProgress && !hasNotifications && <CircleSpinnerInline size={12} />}
        {originAppType && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="w-0 overflow-hidden group-hover:w-auto">
                <AppOriginIcon appType={originAppType} />
              </span>
            </TooltipTrigger>
            <TooltipContent>Initiated on Outlook</TooltipContent>
          </Tooltip>
        )}
      </div>
    </button>
  );
}
